/**
 * TORRAS analytics — PostHog event wiring for the storefront.
 *
 * Extracted from snippets/torras-analytics.liquid (r4, CWV): the ~27KB static
 * body now ships as this cacheable deferred asset instead of inline HTML on
 * every page. Page-specific Liquid values arrive via the small inline config
 * `window.__torrasAnalyticsPage` emitted by that snippet, which must render
 * BEFORE this script's <script defer src> tag (it does — same snippet).
 * Event names, payloads and listener semantics are unchanged.
 */
    (function () {
      'use strict';

      var READY_TIMEOUT_MS = 15000;
      var POLL_MS = 200;

      var queue = [];
      var pollTimer = null;
      var elapsed = 0;

      function posthogReady() {
        return !!(window.posthog && typeof window.posthog.capture === 'function');
      }

      function flush() {
        while (queue.length) {
          var item = queue.shift();
          try {
            window.posthog[item[0]](item[1], item[2], item[3]);
          } catch (e) {
            /* never let analytics break the page */
          }
        }
      }

      function startPolling() {
        if (pollTimer) return;
        pollTimer = setInterval(function () {
          elapsed += POLL_MS;
          if (posthogReady()) {
            clearInterval(pollTimer);
            pollTimer = null;
            flush();
          } else if (elapsed >= READY_TIMEOUT_MS) {
            // PostHog never showed up (blocked, or not installed). Drop the queue
            // rather than growing it for the life of the page.
            clearInterval(pollTimer);
            pollTimer = null;
            queue.length = 0;
          }
        }, POLL_MS);
      }

      function call(method, a, b, c) {
        if (posthogReady()) {
          try {
            window.posthog[method](a, b, c);
          } catch (e) {
            /* swallow */
          }
          return;
        }
        queue.push([method, a, b, c]);
        startPolling();
      }

      /* `opts` is PostHog's own options bag — only used for
         { transport: 'sendBeacon' } on events fired as the page navigates away.
         Existing two-arg callers are unaffected. */
      function capture(name, props, opts) {
        call('capture', name, props, opts);
      }

      /* Same email-as-distinct_id scheme as torras-posthog.liquid and the checkout
         pixel — merges this device's anonymous history into the person. */
      function identify(email, props) {
        call('identify', email, props);
      }

      window.torrasAnalytics = { capture: capture, identify: identify };

      /* Page-specific values come from the inline config emitted by
         snippets/torras-analytics.liquid (window.__torrasAnalyticsPage). */
      var PAGE = window.__torrasAnalyticsPage || {};
      var CURRENCY = PAGE.currency || null;
      var TEMPLATE = PAGE.template || null;

      // Number(null) is 0, so a null compare_at_price would silently become a
      // real price of 0. Absent must stay absent.
      function money(cents) {
        if (cents === null || cents === undefined || cents === '') return null;
        var n = Number(cents);
        return isFinite(n) ? n / 100 : null;
      }

      /* ---------------------------------------------------------------------
       * add_to_cart
       *
       * Every add path in this theme converges on the document-level `cart:update`
       * event (assets/events.js). Three producers, distinguished by detail.data.source:
       *   product-form-component  → PDP add, and quick-add (which reuses the same form)
       *   torras-bundle-ladder    → blocks/torras-bundle-ladder.liquid (its own fetch)
       *   cart-items-component    → a quantity edit inside the cart, NOT an add
       * The error branch of product-form.js reuses the same event with didError:true.
       * ------------------------------------------------------------------- */
      /* The page's own product, when there is one — lets the PDP add path report a title
         and price the cart:update payload doesn't carry. A quick-add fires for some OTHER
         product, so this is only trusted when the ids match. */
      var PAGE_PRODUCT = PAGE.product
        ? {
            id: PAGE.product.id,
            title: PAGE.product.title,
            handle: PAGE.product.handle,
            price: money(PAGE.product.price_cents)
          }
        : null;

      /* Latest known cart, so cart_viewed doesn't have to re-fetch /cart.js on every
         drawer open. Seeded from Liquid, then kept fresh by cart:update. */
      var lastCart = {
        item_count: PAGE.cart ? PAGE.cart.item_count : null,
        total_price: PAGE.cart ? PAGE.cart.total_price : null
      };

      /* variant_id -> quantity as of the last cart we saw. Seeded from Liquid so the very
         first add of a page load is already diffable, then kept fresh on every cart:update.
         Used only as the last resort for identifying an add (see below). */
      function indexCartQty(items) {
        var m = {};
        if (Array.isArray(items)) {
          for (var i = 0; i < items.length; i++) {
            var it = items[i];
            var key = String(it.id != null ? it.id : it.variant_id);
            m[key] = (m[key] || 0) + (it.quantity || 0);
          }
        }
        return m;
      }
      var lastItemQty = indexCartQty(PAGE.cart ? PAGE.cart.items : null);

      document.addEventListener('cart:update', function (event) {
        var detail = event.detail || {};
        var data = detail.data || {};

        // Snapshot before overwriting — the diff needs the cart as it was BEFORE this add.
        var prevItemQty = lastItemQty;

        if (detail.resource && typeof detail.resource.item_count === 'number') {
          lastCart = detail.resource;
          lastItemQty = indexCartQty(detail.resource.items);
        }

        if (data.didError) return;
        if (data.source === 'cart-items-component') return;
        // assets/torras-header.js re-announces the cart after a bfcache restore. Nothing
        // was added — lastCart above is all this listener should take from it.
        if (data.source === 'torras-cart-resync') return;

        // The bundle ladder dispatches on `document`, so event.target is the document
        // itself (nodeType 9) — a query scoped to it would match the first unrelated
        // product form on the page. The detail is the source of truth; the DOM is only
        // consulted for product-form-component, which dispatches on itself.
        var component = null;
        if (event.target && event.target.nodeType === 1 && event.target.closest) {
          component = event.target.closest('product-form-component');
        }

        var origin = 'other';
        if (data.source === 'torras-bundle-ladder') {
          origin = 'bundle_ladder';
        } else if (data.source === 'product-form-component') {
          origin = component && component.hasAttribute('data-t-qa') ? 'quick_add' : 'pdp';
        }

        // Prefer what the producer told us; only fall back to the form's hidden input.
        var variantId = data.variantId ? String(data.variantId) : null;
        if (!variantId && component) {
          var idInput = component.querySelector('[name="id"]');
          if (idInput && idInput.value) variantId = String(idInput.value);
        }

        var productId = data.productId ? String(data.productId) : null;

        // The bundle ladder adds a main item + N add-ons in one call; prices are cents.
        var items = null;
        if (Array.isArray(data.addedItems) && data.addedItems.length) {
          items = data.addedItems.map(function (it) {
            return {
              variant_id: it.variant_id || null,
              product_id: it.product_id || null,
              title: it.title || null,
              price: money(it.price),
              quantity: it.quantity || 1,
              role: it.role || null
            };
          });
        }

        // The main item names the add; a bundle's add-ons ride along in `items`.
        var main = items ? items[0] : null;
        if (!productId && main) productId = main.product_id;
        if (!variantId && main) variantId = main.variant_id;

        var cart = detail.resource || {};

        var title = main ? main.title : null;
        var price = main ? main.price : null;
        var handle = null;
        if ((title === null || price === null) && PAGE_PRODUCT && productId === String(PAGE_PRODUCT.id)) {
          if (title === null) title = PAGE_PRODUCT.title;
          if (price === null) price = PAGE_PRODUCT.price;
          handle = PAGE_PRODUCT.handle;
        }

        /* Quick-add is the gap the two sources above cannot close: it fires for a product
           that is NOT the page's product (so PAGE_PRODUCT is wrong or absent), and
           product-form.js's CartAddEvent carries no title (only source/itemCount/productId/
           sections). Result until 2026-08-17: every collection-page quick-add reported
           product_title:null — 31.6% of all adds, unattributable to a product.
           But `detail.resource` IS the whole /cart.js body, and its line items carry
           product_title / handle / price. Match the variant we just added (falling back to
           the product, for the case where the producer only told us productId) and read the
           name off the cart itself. Runs last, so it never overrides a producer's own value. */
        if ((title === null || price === null || handle === null) && Array.isArray(cart.items)) {
          var added = null;
          for (var ci = 0; ci < cart.items.length; ci++) {
            var line = cart.items[ci];
            if (variantId && String(line.id) === variantId) { added = line; break; }
            if (!added && productId && String(line.product_id) === productId) added = line;
          }
          if (added) {
            // product_title is the product alone; `title` would append the variant and
            // wouldn't match what the PDP path reports.
            if (title === null) title = added.product_title || null;
            if (price === null) price = money(added.price);
            if (handle === null) handle = added.handle || null;
          }
        }

        /* Last resort, for producers that name neither product nor variant:
           cart-recommendations, summer-bundle-builder, torras-3d-configurator, seel,
           wc-bundle. All of them do hand over the fresh cart, so the added line is the
           one whose quantity grew against the previous cart.
           Only trusted when EXACTLY ONE line grew — with two or more we cannot tell which
           add this event describes, and a null is better than a wrong product. */
        if (title === null && Array.isArray(cart.items)) {
          var grown = null;
          var grownCount = 0;
          for (var gi = 0; gi < cart.items.length; gi++) {
            var gl = cart.items[gi];
            if ((gl.quantity || 0) > (prevItemQty[String(gl.id)] || 0)) {
              grown = gl;
              grownCount++;
            }
          }
          if (grownCount === 1 && grown) {
            title = grown.product_title || null;
            if (price === null) price = money(grown.price);
            if (handle === null) handle = grown.handle || null;
            if (!productId && grown.product_id) productId = String(grown.product_id);
            if (!variantId && grown.id) variantId = String(grown.id);
          }
        }

        // data.itemCount means different things per producer: product-form.js sends
        // the quantity just added, torras-bundle-ladder sends the whole cart's count.
        // Don't collapse them into one field.
        var addedQty = null;
        var cartCount = typeof cart.item_count === 'number' ? cart.item_count : null;
        if (data.source === 'product-form-component' && typeof data.itemCount === 'number') {
          addedQty = data.itemCount;
        } else if (data.source === 'torras-bundle-ladder' && typeof data.itemCount === 'number') {
          cartCount = data.itemCount;
        }
        if (addedQty === null && items) {
          addedQty = items.reduce(function (n, it) { return n + (it.quantity || 0); }, 0);
        }

        capture('add_to_cart', {
          add_source: origin,
          raw_source: data.source || null,
          variant_id: variantId,
          product_id: productId,
          product_title: title,
          product_handle: handle,
          price: price,
          quantity: addedQty,
          items: items,
          bundle_size: items ? items.length : null,
          cart_item_count: cartCount,
          cart_total_price: money(cart.total_price),
          currency: CURRENCY
        });
      });

      /* ---------------------------------------------------------------------
       * express_checkout_clicked  —  the Buy Now / Shop Pay button
       *
       * `{{ form | payment_button }}` sends the buyer straight into Shop Pay. It
       * never POSTs /cart/add, so it never fires add_to_cart. Measured 2026-10-06:
       * after that button shipped (09-29 18:55), 27.1% of mobile ordering sessions
       * had ZERO add_to_cart in the whole session (baseline 3.9%), which dragged the
       * measured add-to-cart rate down 22% with no change in real behaviour. This
       * event puts that path back on the map.
       *
       * Why a plain document listener is enough (verified on the live PDP, same day):
       * <shop-pay-wallet-button> renders in the LIGHT DOM — not an iframe.
       * elementFromPoint() at the button's centre returns the button itself, and a
       * composed event reaches document with closest() still resolving to the host.
       * If Shopify ever moves it into an iframe this listener goes silent rather
       * than wrong — check `express_checkout_clicked` volume against the count of
       * zero-add_to_cart orders when auditing.
       *
       * click (not pointerdown): pointerdown also fires when a thumb lands on the
       * button mid-scroll. Capture phase runs before Shopify's own handler, and
       * sendBeacon survives the navigation that follows.
       * ------------------------------------------------------------------- */
      document.addEventListener('click', function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var host = target.closest('shopify-accelerated-checkout, shopify-accelerated-checkout-cart');
        if (!host) return;

        var form = host.closest('form');
        var ladder = host.closest('[data-tbl-shoppay]');
        var surface = ladder ? 'pdp_bundle_ladder'
          : (host.tagName.toLowerCase().indexOf('cart') !== -1 ? 'cart' : 'other');

        /* "More payment options" sits inside the same host but is a different intent. */
        if (target.closest('more-payment-options-link')) {
          capture('more_payment_options_clicked', { surface: surface },
                  { transport: 'sendBeacon' });
          return;
        }

        /* Shopify picks the wallet per buyer; keep the raw tag so a new one
           (Apple Pay, Google Pay, PayPal) shows up as itself rather than silently
           folding into shop_pay. */
        var btn = target.closest('shop-pay-wallet-button, apple-pay-button, google-pay-button')
               || (target.tagName && target.tagName.indexOf('-') !== -1 ? target : null);
        var tag = btn && btn.tagName ? btn.tagName.toLowerCase() : '';
        var wallet = tag.indexOf('shop-pay') !== -1 ? 'shop_pay'
                   : tag.indexOf('apple') !== -1 ? 'apple_pay'
                   : tag.indexOf('google') !== -1 ? 'google_pay'
                   : (tag || 'unknown');

        var idInput = form && form.querySelector('input[name="id"]');
        var qtyInput = form && form.querySelector('input[name="quantity"]');
        var variantId = idInput && idInput.value ? Number(idInput.value) : null;

        capture('express_checkout_clicked', {
          wallet: wallet,
          surface: surface,
          variant_id: variantId,
          quantity: qtyInput && qtyInput.value ? Number(qtyInput.value) : 1,
          product_id: PAGE_PRODUCT ? PAGE_PRODUCT.id : null,
          product_handle: PAGE_PRODUCT ? PAGE_PRODUCT.handle : null,
          product_title: PAGE_PRODUCT ? PAGE_PRODUCT.title : null,
          currency: CURRENCY
        }, { transport: 'sendBeacon' });
      }, true);

      /* ---------------------------------------------------------------------
       * variant_selected
       *
       * Horizon dispatches VariantUpdateEvent ('variant:update', assets/events.js)
       * after every variant change. For a screen-protector store the chosen phone
       * model / pack size is the demand signal — it fires far more often than
       * add_to_cart and feeds SKU planning.
       * ------------------------------------------------------------------- */
      document.addEventListener('variant:update', function (event) {
        var detail = event.detail || {};
        var v = detail.resource;
        if (!v || !v.id) return;

        capture('variant_selected', {
          variant_id: String(v.id),
          variant_title: v.title || null,
          sku: v.sku || null,
          available: typeof v.available === 'boolean' ? v.available : null,
          price: money(v.price),
          product_id: detail.data && detail.data.productId ? String(detail.data.productId) : null,
          source_id: detail.sourceId || null,
          currency: CURRENCY
        });
      });

      /* ---------------------------------------------------------------------
       * newsletter signup
       *
       * All email capture in this theme is a native {% form 'customer' %} POST
       * (footer email-signup block, torras-wc-lead-capture) — full page reload,
       * so we log twice: at submit (capture phase, and identify() so the email
       * merges this device's anonymous history), and on the redirected page,
       * which Shopify marks with ?customer_posted=true (the submit-time event
       * can be lost to the unload; the confirm is the reliable count).
       * ------------------------------------------------------------------- */
      document.addEventListener('submit', function (event) {
        var form = event.target;
        if (!form || form.nodeName !== 'FORM' || !form.querySelector) return;

        var emailInput = form.querySelector('input[name="contact[email]"]');
        if (!emailInput) return;

        var typeInput = form.querySelector('input[name="form_type"]');
        // 'customer' = newsletter/lead form; 'contact' (contact page) stays out.
        if (!typeInput || typeInput.value !== 'customer') return;

        var email = (emailInput.value || '').trim().toLowerCase();
        if (!email || email.indexOf('@') === -1) return;

        try {
          sessionStorage.setItem('torras_nl_email', email);
        } catch (e) {
          /* storage blocked — confirm event just won't identify */
        }

        identify(email, { email: email });

        var section = form.closest('[id^="shopify-section-"]');
        capture('newsletter_signup_submitted', {
          form_location: section ? section.id : null
        });
      }, true);

      if (window.location.search.indexOf('customer_posted=true') !== -1) {
        var storedEmail = null;
        try {
          storedEmail = sessionStorage.getItem('torras_nl_email');
          sessionStorage.removeItem('torras_nl_email');
        } catch (e) {
          /* ignore */
        }
        if (storedEmail) identify(storedEmail, { email: storedEmail });
        capture('newsletter_signup_confirmed', {});
      }

      /* --------------------------------------------------------------------- */

      if (PAGE.search) {
        capture('search_performed', PAGE.search);
      }

      if (PAGE.product) {
        capture('product_viewed', {
          product_id: PAGE.product.id,
          product_handle: PAGE.product.handle,
          product_title: PAGE.product.title,
          product_type: PAGE.product.type,
          variant_id: PAGE.product.variant_id,
          variant_title: PAGE.product.variant_title,
          price: money(PAGE.product.price_cents),
          compare_at_price: money(PAGE.product.compare_at_price_cents),
          available: PAGE.product.available,
          currency: CURRENCY,
          template: PAGE.product.template_suffix
        });
      }

      /* ---------------------------------------------------------------------
       * cart_viewed
       *
       * This theme's cart is a DRAWER — /cart is a page shoppers effectively never
       * land on, so the template-gated capture below fired ~never and the step between
       * add_to_cart and checkout_started was invisible. The drawer is the real event.
       *
       * DialogComponent dispatches `dialog:open` WITHOUT bubbles, so it never reaches
       * document on the bubble path — but a non-bubbling event still runs the capture
       * phase down to its target, hence the `true` below.
       * ------------------------------------------------------------------- */
      document.addEventListener('dialog:open', function (event) {
        var el = event.target;
        if (!el || !el.tagName || el.tagName.toLowerCase() !== 'cart-drawer-component') return;

        capture('cart_viewed', {
          view_source: 'drawer',
          cart_item_count: typeof lastCart.item_count === 'number' ? lastCart.item_count : null,
          cart_total_price: money(lastCart.total_price),
          currency: CURRENCY
        });
      }, true);

      if (TEMPLATE === 'cart') {
        capture('cart_viewed', {
          view_source: 'page',
          cart_item_count: lastCart.item_count,
          cart_total_price: money(lastCart.total_price),
          currency: CURRENCY
        });
      }

      if (PAGE.collection) {
        capture('collection_viewed', PAGE.collection);
      }

      /* =====================================================================
       * Header
       *
       * The header emitted nothing at all: autocapture is off, and none of its
       * links are Horizon components that dispatch events, so every click on the
       * logo, the mega menus and the search modal was invisible.
       *
       * All of it is delegated from `document`. The header section's HTML gets
       * rewritten whenever a cart-drawer morph re-renders it (the drawer is
       * rendered inside that section), so listeners bound to .t-hdr or to the
       * links themselves are silently dropped — the section's own JS already
       * learned this; see the delegation note in sections/torras-header.liquid.
       * ================================================================== */

      // The breakpoint the header switches nav models at: above it, desktop bar +
      // CSS :hover mega; below it, burger drawer. Kept live — a resize flips it.
      var DESKTOP_NAV = window.matchMedia('(min-width: 1000px)');

      function navMode() {
        return DESKTOP_NAV.matches ? 'desktop' : 'drawer';
      }

      function text(el) {
        if (!el) return null;
        var s = (el.textContent || '').replace(/\s+/g, ' ').trim();
        return s || null;
      }

      /* Re-queried whenever the node we cached is no longer in the document —
         that's the morph. Used as a cheap containment gate on mouseover, which
         fires far too often to run closest() on unconditionally. */
      var headerEl = null;
      function header() {
        if (!headerEl || !headerEl.isConnected) headerEl = document.querySelector('.t-hdr');
        return headerEl;
      }

      /* A link's own label. Several header links wrap a secondary line inside the
         same <a> — a trigger's subtitle, a device chip's "Popular" badge — and a
         plain textContent read glues them onto the end ("iPhone 17 Pro MaxPopular",
         "DevicesApple · Nintendo"). Each of those lands as its own value in a
         breakdown, so strip them at the source; there's no cleaning them after. */
      var LABEL_NOISE =
        '.t-hdr__sub, .t-hdr-dev__badge, .t-hdr-card__tag, .t-hdr-card__sub,' +
        '.t-hdr-fcard__label, .t-hdr-fcard__sub, .t-hdr-flink__meta, .t-hdr-lens__sub, .t-hdr-lens__tag,' +
        '.t-hdr-col__brand-sub, .t-hdr-chip__badge, .t2-card__hk, .t2-card__cv';

      function linkLabel(link) {
        var clone = link.cloneNode(true);
        clone.querySelectorAll(LABEL_NOISE).forEach(function (n) {
          n.remove();
        });
        // Image links (a series card's art) carry no text, only an aria-label.
        return text(clone) || link.getAttribute('aria-label') || null;
      }

      /* A mega trigger's own label, minus its "New!" accent span — the same
         first-child-node read the section uses to title its mobile sub-panels. */
      function triggerLabel(item) {
        if (!item) return null;
        var el = item.querySelector('.t-hdr__label > span') || item.querySelector('.t-hdr__label');
        if (!el) return null;
        var first = el.childNodes[0];
        var raw = (first ? first.textContent : el.textContent) || '';
        raw = raw.replace(/\s+/g, ' ').trim();
        return raw || null;
      }

      /* A menu's stable identity. Translate & Adapt localises the trigger text,
         so menu_label alone splits one menu across locales (Devices / 设备 /
         Vidrio…) in a breakdown. The trigger's route is the same everywhere
         once the locale prefix is dropped — Shopify keeps handles constant
         across locales. Label-only triggers (no link) stay null. */
      function menuId(item) {
        var trig = item ? item.querySelector('.t-hdr__trigger') : null;
        var href = trig ? trig.getAttribute('href') : null;
        if (!href) return null;
        var path;
        try {
          path = new URL(href, location.origin).pathname;
        } catch (e) {
          return href;
        }
        var m = path.match(/\/(collections|pages|products|blogs)\/.*$/);
        return m ? m[0] : path;
      }

      /* The device the V2 panels' gate is narrowed to — same key the section
         reads/writes (localStorage 'torras_gate_device', shared site-wide).
         Rides on every header event so open→click funnels can split gated vs
         ungated sessions without a join. */
      function gateDevice() {
        try {
          return localStorage.getItem('torras_gate_device') || null;
        } catch (e) {
          return null;
        }
      }

      /* Which piece of the header was clicked. First match wins, so a card's
         "View all" is classified before the card that contains it. */
      var NAV_COMPONENTS = [
        ['.t-hdr__logo', 'logo'],
        ['.t-hdr-card__all', 'view_all'],
        ['.t-hdr-strip__all', 'view_all'],
        ['.t-hdr-fit__all', 'view_all'],
        ['.t-hdr-col__viewall', 'view_all'],
        ['.t2-group__va', 'view_all'],
        ['.t2-strip__right', 'view_all'],
        ['.t-hdr__panel-cta', 'panel_cta'],
        ['.t-hdr-card__img', 'series_card'],
        ['.t-hdr-card__name', 'series_card'],
        // V2 cards reuse the old component names on purpose: a device tile and
        // a product/family card play the same role the old device chip and
        // series card did, and existing insights keep aggregating across the
        // relaunch. Attribute selector first — a device tile IS a .t2-card.
        ['.t2-card[data-device-id]', 'device_chip'],
        ['.t2-card', 'series_card'],
        ['.t-hdr-chip', 'tier_chip'],
        ['.t-hdr-dev', 'device_chip'],
        ['.t-hdr-acc', 'accessory'],
        ['.t-hdr-elink', 'explore_link'],
        ['.t-hdr-flink', 'feat_link'],
        ['.t-hdr-fcard', 'feat_card'],
        ['.t-hdr-col__brand', 'explore_brand'],
        ['.t-hdr-lens__img', 'glass_foot'],
        ['.t-hdr-lens__head', 'glass_foot'],
        ['.t-hdr__trigger', 'mega_trigger'],
        ['.t-hdr__plain', 'top_link'],
        ['.t-hdr__d-util', 'utility'],
        ['.t-hdr__icon-btn', 'utility']
      ];

      /* The labelled block a link sits in. "SunView → Ultra" ranks a tier chip in
         a way a bare index never could — every card repeats the same chip labels. */
      var NAV_GROUPS = [
        ['.t2-group', '.t2-group__title'],
        ['.t2-strip', '.t2-strip__label'],
        ['.t-hdr-card', '.t-hdr-card__name'],
        ['.t-hdr-strip', '.t-hdr-strip__label'],
        ['.t-hdr-fit', '.t-hdr-fit__title'],
        ['.t-hdr-acc-row', '.t-hdr-acc-row__label'],
        ['.t-hdr-col', '.t-hdr-col__label']
      ];

      function navArea(link) {
        if (link.closest('.t-hdr__logo')) return 'logo';
        if (link.closest('.t-hdr__panel')) return DESKTOP_NAV.matches ? 'mega_panel' : 'drawer_subpanel';
        if (link.closest('.t-hdr__d-foot')) return 'drawer_foot';
        if (link.closest('.t-hdr__right')) return 'utility';
        if (link.closest('.t-hdr__nav')) return DESKTOP_NAV.matches ? 'nav_bar' : 'drawer_root';
        return 'other';
      }

      /* ---------------------------------------------------------------------
       * header_nav_clicked — one event for every navigation out of the header.
       * ------------------------------------------------------------------- */
      document.addEventListener('click', function (event) {
        var target = event.target;
        if (!target || !target.closest || !target.closest('.t-hdr')) return;

        // Every real header navigation is an <a>. The buttons in here are the
        // burger, drawer back/close and search — all of which either own a
        // separate event or aren't navigation at all.
        var link = target.closest('a');
        if (!link) return;

        // A mega trigger below the breakpoint is a panel opener, not a link:
        // the section preventDefault()s it, so it is not a navigation.
        if (link.classList.contains('t-hdr__trigger') && !DESKTOP_NAV.matches) return;

        // A device-gate pick is an <a> but not a navigation either — the section
        // preventDefault()s it and filters the open panel in place.
        // header_device_selected owns that click.
        if (link.closest('[data-devsel]')) return;

        var selector = null;
        var componentEl = null;
        var component = null;
        for (var i = 0; i < NAV_COMPONENTS.length; i++) {
          componentEl = link.closest(NAV_COMPONENTS[i][0]);
          if (componentEl) {
            selector = NAV_COMPONENTS[i][0];
            component = NAV_COMPONENTS[i][1];
            break;
          }
        }

        var group = null;
        var groupLabel = null;
        for (var j = 0; j < NAV_GROUPS.length; j++) {
          group = link.closest(NAV_GROUPS[j][0]);
          if (group) {
            groupLabel = text(group.querySelector(NAV_GROUPS[j][1]));
            // A flat V2 panel's single group renders no visible head; the rail
            // label it would have carried is still on the container.
            if (!groupLabel) groupLabel = group.getAttribute('data-rail-label') || null;
            break;
          }
        }

        // An index is only meaningful next to the group and the component type
        // it was counted within — "3rd tier_chip inside the SunView card".
        // [hidden] peers don't count: the By Device panel keeps list-only cards
        // (device-picker feed, never shown) between the visible tiles.
        var position = null;
        if (group && componentEl && selector) {
          var peers = group.querySelectorAll(selector);
          var seen = 0;
          for (var k = 0; k < peers.length; k++) {
            if (peers[k].hasAttribute('hidden')) continue;
            seen++;
            if (peers[k] === componentEl) {
              position = seen;
              break;
            }
          }
        }

        capture('header_nav_clicked', {
          nav_area: navArea(link),
          nav_component: component,
          label: linkLabel(link),
          url: link.getAttribute('href') || null,
          menu_label: triggerLabel(link.closest('.t-hdr__item')),
          menu_id: menuId(link.closest('.t-hdr__item')),
          group_label: groupLabel,
          position: position,
          gate_device: gateDevice(),
          nav_mode: navMode(),
          template: TEMPLATE
        });

        // A click that navigates unloads the page, so the drawer never "closes" —
        // without this, the one path missing from header_drawer_closed would be
        // the converting one, which is the path most worth counting.
        if (!DESKTOP_NAV.matches && link.closest('.t-hdr__nav')) {
          closeDrawerSession('navigated', true);
        }
      });

      /* ---------------------------------------------------------------------
       * Drawer depth marker
       *
       * Below the breakpoint, tapping a mega trigger opens the drawer's second
       * level. header_drawer_closed reports how deep a session went (max_depth),
       * and this tap is the only place that depth is observable.
       * ------------------------------------------------------------------- */
      document.addEventListener('click', function (event) {
        if (DESKTOP_NAV.matches) return;
        var target = event.target;
        if (!target || !target.closest) return;
        if (!target.closest('.t-hdr__trigger')) return;
        drawerMaxDepth = 1; // a category sub-panel is the drawer's second level
      });

      /* ---------------------------------------------------------------------
       * header_device_gate_opened / header_device_selected — the V2 panels'
       * device picker (.t2-devsel).
       *
       * A pick is an <a> but never a navigation: the section preventDefault()s
       * it, filters the open panel in place and swaps folded cards to the
       * member matching the model — so header_nav_clicked excludes the picker
       * (above) and these two own its funnel. The third step, "filtered click",
       * is header_nav_clicked's gate_device property.
       *
       * Capture phase on purpose: the section's handlers run on bubble, so
       * here localStorage still holds the PREVIOUS device and the picker's
       * .is-open still reads the pre-click state.
       * ------------------------------------------------------------------- */
      document.addEventListener(
        'click',
        function (event) {
          var target = event.target;
          if (!target || !target.closest || !target.closest('.t2-devsel')) return;

          var opener = target.closest('[data-tfs-open]');
          if (opener) {
            var host = opener.closest('.t2-devsel');
            var root = host.querySelector('[data-tfs-root]');
            // The same button collapses an open picker — only count the open.
            if (root && root.classList.contains('is-open')) return;
            var item = opener.closest('.t-hdr__item');
            capture('header_device_gate_opened', {
              menu_label: triggerLabel(item),
              menu_id: menuId(item),
              gate_device: gateDevice(),
              nav_mode: navMode(),
              template: TEMPLATE
            });
            return;
          }

          var pick = target.closest('.tfs-item');
          // "View all N models" is styled as an item but only expands the list.
          if (!pick || pick.hasAttribute('data-tfs-more')) return;
          var isAll = pick.classList.contains('tfs-item--all');
          var brandWrap = pick.closest('[data-tfs-group]');
          var pickItem = pick.closest('.t-hdr__item');
          capture('header_device_selected', {
            menu_label: triggerLabel(pickItem),
            menu_id: menuId(pickItem),
            device: isAll ? null : text(pick.querySelector('.tfs-item-name')),
            device_brand: brandWrap ? text(brandWrap.querySelector('.tfs-ghead')) : null,
            cleared: isAll,
            previous_device: gateDevice(),
            nav_mode: navMode(),
            template: TEMPLATE
          });
        },
        true
      );

      /* ---------------------------------------------------------------------
       * header_search_opened / header_search_suggestion_clicked
       *
       * search_performed (above) only fires on the /search template — i.e. only
       * once a shopper commits to a full results page. The modal itself, and the
       * recommended chips in its empty state, were invisible: an opened-then-
       * abandoned search looked exactly like no search at all.
       *
       * DialogComponent dispatches dialog:open on itself WITHOUT bubbles (same as
       * the cart drawer above), hence the capture-phase listener.
       * ------------------------------------------------------------------- */
      var lastSearchTrigger = null;

      document.addEventListener(
        'click',
        function (event) {
          var target = event.target;
          if (!target || !target.closest || !target.closest('.t-hdr')) return;
          if (target.closest('.t-hdr__d-search')) lastSearchTrigger = 'drawer_search';
          else if (target.closest('.t-hdr__icon-btn[aria-haspopup="dialog"]')) lastSearchTrigger = 'header_icon';
        },
        true
      );

      document.addEventListener(
        'dialog:open',
        function (event) {
          var el = event.target;
          if (!el || el.id !== 'search-modal') return;
          capture('header_search_opened', {
            // Anything that opens the modal from outside the header (a keyboard
            // shortcut, another section's search entry) still counts, as 'other'.
            trigger: lastSearchTrigger || 'other',
            nav_mode: navMode(),
            template: TEMPLATE
          });
          lastSearchTrigger = null;
        },
        true
      );

      var SEARCH_SUGGESTIONS = [
        ['.predictive-search-recommended__chip', 'quick_chip'],
        ['.predictive-search-results__card--product', 'product'],
        ['.predictive-search-results__card', 'resource']
      ];

      document.addEventListener('click', function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var modal = target.closest('.search-modal');
        if (!modal) return;
        var link = target.closest('a');
        if (!link) return;

        var type = null;
        var suggestionEl = null;
        var suggestionSel = null;
        for (var i = 0; i < SEARCH_SUGGESTIONS.length; i++) {
          suggestionEl = link.closest(SEARCH_SUGGESTIONS[i][0]);
          if (suggestionEl) {
            suggestionSel = SEARCH_SUGGESTIONS[i][0];
            type = SEARCH_SUGGESTIONS[i][1];
            break;
          }
        }
        if (!type) return;

        var scope = modal.querySelector('.predictive-search-dropdown') || modal;
        var position = null;
        var idx = Array.prototype.indexOf.call(scope.querySelectorAll(suggestionSel), suggestionEl);
        if (idx !== -1) position = idx + 1;

        var input = modal.querySelector('.search-input');

        capture('header_search_suggestion_clicked', {
          suggestion_type: type,
          label: linkLabel(link),
          url: link.getAttribute('href') || null,
          // Empty on a quick chip — those live in the empty state, which is the
          // point of them: they're what a shopper picks INSTEAD of typing.
          query: input ? (input.value || '').trim() : null,
          position: position,
          nav_mode: navMode(),
          template: TEMPLATE
        });
      });

      /* ---------------------------------------------------------------------
       * header_drawer_opened / header_drawer_closed
       *
       * The close event is the one carrying the finding: a drawer opened, paged
       * a level deep and dismissed without a single tap is a design failure you
       * can count — but only because max_depth and did_navigate ride along with
       * it. header_drawer_opened is here to be its denominator.
       *
       * Open/close is a class on the header root, flipped from five places in
       * the section (burger, close button, scrim, Escape, breakpoint change).
       * Watching the class catches every one of them; re-deriving the conditions
       * here would just be a copy waiting to drift from the section's.
       * ------------------------------------------------------------------- */
      var drawerOpenAt = null;
      var drawerMaxDepth = 0;
      var drawerCloseHint = null;
      var drawerObserved = null;
      var drawerObserver = null;

      function openDrawerSession() {
        drawerOpenAt = Date.now();
        drawerMaxDepth = 0;
        capture('header_drawer_opened', { nav_mode: navMode(), template: TEMPLATE });
      }

      function closeDrawerSession(reason, didNavigate) {
        if (drawerOpenAt === null) return;
        var openedAt = drawerOpenAt;
        drawerOpenAt = null; // first close wins; a navigation beats the class flip
        capture('header_drawer_closed', {
          close_reason: reason,
          did_navigate: !!didNavigate,
          // 0 = never left the root list, 1 = opened a category sub-panel.
          max_depth: drawerMaxDepth,
          dwell_ms: Date.now() - openedAt,
          nav_mode: navMode(),
          template: TEMPLATE
        });
        drawerCloseHint = null;
      }

      function watchDrawer() {
        var h = header();
        if (!h || h === drawerObserved) return;
        if (drawerObserver) drawerObserver.disconnect();
        drawerObserved = h;
        drawerObserver = new MutationObserver(function () {
          var open = h.classList.contains('is-drawer-open');
          if (open && drawerOpenAt === null) openDrawerSession();
          else if (!open && drawerOpenAt !== null) closeDrawerSession(drawerCloseHint || 'other', false);
        });
        drawerObserver.observe(h, { attributes: true, attributeFilter: ['class'] });
      }
      watchDrawer();

      document.addEventListener(
        'click',
        function (event) {
          var target = event.target;
          if (!target || !target.closest) return;
          // The burger is the only way in, so re-point the observer here: it's the
          // one moment we know a session is about to start, and a morph may have
          // replaced the node we were watching since the last one.
          if (target.closest('.t-hdr__burger')) {
            watchDrawer();
            drawerCloseHint = 'burger';
          } else if (target.closest('.t-hdr__d-close')) {
            drawerCloseHint = 'close_button';
          } else if (target.closest('.t-hdr__scrim')) {
            drawerCloseHint = 'scrim';
          }
        },
        true
      );

      document.addEventListener(
        'keydown',
        function (event) {
          if (event.key === 'Escape') drawerCloseHint = 'escape';
        },
        true
      );

      /* ---------------------------------------------------------------------
       * In-page navigation on the campaign landing boards
       *   anchor_nav_clicked — EVERY same-page `#` jump: the sticky tab bar
       *                        (sections/torras-rewards-anchornav) and any link ops
       *                        typed into section copy. `nav_area` tells them apart.
       *   kit_tab_clicked    — the category tabs (sections/torras-kit-builder)
       *   kit_more_clicked   — each panel's Show more / Show less fold (phones only)
       *
       * These are the whole steering wheel of the iPhone 18 board: everything below
       * the hero is reached either by scrolling past it or through one of them. Until
       * now only the add at the end of that path was counted, so which route produced
       * it was unreadable.
       *
       * The anchor listener is deliberately generic (`a[href^="#"]` minus a skip list)
       * rather than bound to the bar's class. The second jump link asked for on this
       * page — "Not sure which one? Compare all →" — is rich-text a merchant typed into
       * a stock product-list block: it carries no class of its own and its copy can be
       * rewritten in the editor at any time. Anything narrower stops counting the day
       * ops edits the sentence.
       *
       * Both listeners are document-level and run in the CAPTURE phase. Delegation
       * because the kit strip is re-rendered by section:load in the editor and the bar
       * is rebuilt whenever ops reorders blocks; capture because the kit section binds
       * its own handler on the tab element (target phase) — on the bubble phase the
       * "previous" tab has already been repainted and every previous_* property would
       * just echo the destination. Same for the bar's `.is-active`.
       *
       * Nothing here changes the URL or unloads the page, so there is no beacon race.
       *
       * Deliberately NOT stable across a full-page translation: `label` is on-screen
       * text. `target_id` is ops-set and never translated — prefer it as the breakdown
       * key; for the kit tabs `index` plays that role.
       * ------------------------------------------------------------------- */

      /* Which section instance a control belongs to. Shopify's wrapper id is
         `shopify-section-template--<template id>__<key>`; the tail is the key in
         templates/*.json (`subnav`, `kit`) and is the only half that survives a
         template re-save — the numeric part changes. Same read the retired summer
         tracker used for trigger_section. */
      function sectionKey(el) {
        var wrap = el && el.closest ? el.closest('[id^="shopify-section-"]') : null;
        if (!wrap) return null;
        var id = wrap.id.replace(/^shopify-section-/, '');
        var cut = id.lastIndexOf('__');
        return cut === -1 ? id : id.slice(cut + 2);
      }

      function anchorId(link) {
        var href = link && link.getAttribute ? link.getAttribute('href') || '' : '';
        if (href.charAt(0) !== '#') return href || null;
        return href.slice(1) || null;
      }

      /* Not every same-page `#` is a jump. `#` alone is a JS hook; a carousel dot and
         a tab control are `<a href="#…">` that move a widget, not the reader. The
         header owns its own event. */
      var ANCHOR_SKIP = '.slider-counter__link, [role="tab"], .t-hdr';

      /* Where the reader was when they jumped. The sticky bar's `.is-active` is the
         only thing on the page that knows, so a body link borrows the page's bar when
         there is one — that is what makes `direction` meaningful for a link typed into
         copy, not just for the bar's own tabs. */
      function activeAnchorLink() {
        var bar = document.querySelector('.t-rw-nav');
        return bar ? bar.querySelector('.t-rw-nav__link.is-active') : null;
      }

      /* forward = skipping ahead, back = returning to re-read. Compared by document
         position rather than by index in the bar: half the jumps on the iPhone 18
         board point at sections the bar does not list (#i18-compare), and those are
         exactly the ones worth knowing the direction of. */
      function jumpDirection(fromEl, toEl) {
        if (!fromEl || !toEl) return null;
        if (fromEl === toEl) return 'same';
        var rel = fromEl.compareDocumentPosition(toEl);
        if (rel & Node.DOCUMENT_POSITION_FOLLOWING) return 'forward';
        if (rel & Node.DOCUMENT_POSITION_PRECEDING) return 'back';
        return null;
      }

      document.addEventListener(
        'click',
        function (event) {
          var t = event.target;
          if (!t || !t.closest) return;
          var link = t.closest('a[href]');
          if (!link) return;

          var href = link.getAttribute('href') || '';
          if (href.charAt(0) !== '#' || href.length < 2) return;
          if (link.closest(ANCHOR_SKIP)) return;

          var bar = link.closest('.t-rw-nav');
          var barLinks = bar ? [].slice.call(bar.querySelectorAll('.t-rw-nav__link')) : [];
          /* Read BEFORE the jump: the bar repaints .is-active from its own scroll
             handler, so this is only truthful here, on the capture phase. */
          var from = bar ? bar.querySelector('.t-rw-nav__link.is-active') : activeAnchorLink();

          var targetId = anchorId(link);
          var toEl = targetId ? document.getElementById(targetId) : null;
          var fromId = from ? anchorId(from) : null;
          var fromEl = fromId ? document.getElementById(fromId) : null;

          capture('anchor_nav_clicked', {
            // anchor_bar = the sticky tab bar; body_link = an <a href="#…"> ops typed
            // into section copy (the "Compare all" line under the new-products header).
            nav_area: bar ? 'anchor_bar' : 'body_link',
            target_id: targetId,
            // An anchor whose section was renamed still renders as a link and silently
            // does nothing. false here is a broken link you can count.
            target_exists: !!toEl,
            label: text(link),
            index: bar ? barLinks.indexOf(link) : null,
            tab_count: bar ? barLinks.length : null,
            from_target_id: fromId,
            from_label: text(from),
            direction: jumpDirection(fromEl, toEl),
            is_current: !!fromId && fromId === targetId,
            // The section the LINK sits in — for a body link this is the copy that
            // sent them (`new_products`), which the target alone never tells you.
            nav_section: sectionKey(link),
            template: TEMPLATE
          });
        },
        true
      );

      /* How many cards the tab is about to reveal. An empty tab is this section's
         known failure mode — a device collection with no product of that type
         renders a tab that opens onto nothing (see the paginate comment in
         sections/torras-kit-builder.liquid). products_in_tab: 0 counts it. */
      function kitPanelCards(tab) {
        var id = tab.getAttribute('aria-controls');
        var panel = id ? document.getElementById(id) : null;
        if (!panel) return null;
        return panel.querySelectorAll('.t-kit__card').length;
      }

      document.addEventListener(
        'click',
        function (event) {
          var t = event.target;
          if (!t || !t.closest) return;
          var tab = t.closest('.t-kit__tab');
          if (!tab) return;

          var strip = tab.closest('[data-kit-tabs]');
          var tabs = strip ? [].slice.call(strip.querySelectorAll('.t-kit__tab')) : [tab];
          var open = strip ? strip.querySelector('.t-kit__tab.is-on') : null;

          capture('kit_tab_clicked', {
            label: text(tab),
            index: tabs.indexOf(tab),
            tab_count: tabs.length,
            previous_label: text(open),
            previous_index: open ? tabs.indexOf(open) : null,
            is_reselect: !!open && open === tab,
            products_in_tab: kitPanelCards(tab),
            kit_section: sectionKey(tab),
            template: TEMPLATE
          });
        },
        true
      );

      /* Show more / Show less under a kit panel (phones, `mobile_layout: grid` only —
         desktop never renders the button). One button per tab panel, same capture-phase
         reason as the tabs: the section flips aria-expanded on its root's bubble phase,
         so only here does it still say what the shopper was looking at when they tapped.
         `action` is the breakdown key; `label` is on-screen text and gets translated. */
      document.addEventListener(
        'click',
        function (event) {
          var t = event.target;
          if (!t || !t.closest) return;
          var more = t.closest('.t-kit__more');
          if (!more) return;

          var panel = more.closest('[data-kit-panel]');
          var tabId = panel ? panel.getAttribute('aria-labelledby') : null;
          var tab = tabId ? document.getElementById(tabId) : null;
          var strip = tab ? tab.closest('[data-kit-tabs]') : null;
          var cards = panel ? panel.querySelectorAll('.t-kit__card').length : null;
          var folded = panel ? panel.querySelectorAll('[data-kit-fold]').length : null;

          capture('kit_more_clicked', {
            action: more.getAttribute('aria-expanded') === 'true' ? 'collapse' : 'expand',
            label: text(more),
            tab_label: text(tab),
            tab_index: panel ? Number(panel.getAttribute('data-kit-panel')) : null,
            tab_count: strip ? strip.querySelectorAll('.t-kit__tab').length : null,
            // Cards in the panel / how many of them sit behind the button. The shown-on-
            // open count is products_in_tab - cards_folded.
            products_in_tab: cards,
            cards_folded: folded,
            kit_section: sectionKey(more),
            template: TEMPLATE
          });
        },
        true
      );

      /* Client-side chips + pager on a product list (sections/product-list.liquid,
         "TORRAS filter & pagination"). The section owns the state and announces each
         change on the document AFTER it has applied, so `results` is the card count
         the shopper is now looking at, not the one they left. `axis` is model / type,
         or `reset` for the "Clear filters" link under an empty grid. */
      document.addEventListener('torras:product_filter', function (event) {
        var d = event.detail || {};
        capture('product_filter_clicked', {
          axis: d.axis,
          label: d.label,
          value: d.value,
          index: d.index,
          chip_count: d.chip_count,
          previous_label: d.previous_label,
          previous_value: d.previous_value,
          // Tapping the lit chip clears the axis — that is a real "show me everything",
          // not a no-op, so it stays a click with is_reselect: true.
          is_reselect: !!d.is_reselect,
          results: d.results,
          page_count: d.page_count,
          list_section: d.section,
          template: TEMPLATE
        });
      });

      document.addEventListener('torras:product_list_page', function (event) {
        var d = event.detail || {};
        capture('product_list_page_changed', {
          page: d.page,
          previous_page: d.previous_page,
          page_count: d.page_count,
          page_size: d.page_size,
          results: d.results,
          // prev / next arrow, or a page number
          control: d.control,
          list_section: d.section,
          template: TEMPLATE
        });
      });
    })();
